import { Button } from "@/components/Button";
import ConsentRecordDetail from "@/components/Common/AuditLogs/ContactDetails/ConsentRecordDetail";
import ContactHistoryTable from "@/components/Common/AuditLogs/ContactHistoryTable";
import Shimmer from "@/components/Shimmer";
import { HeaderTextBlock } from "@/components/Typography";
import {
  useGetContactDetails,
  useGetContactHistoryDetails,
} from "@/requests/hooks/collect";
import { faCaretLeft } from "@fortawesome/pro-solid-svg-icons";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

interface IProps {
  channel: IContactChannel;
}

const ContactDetailsController = ({ channel }: IProps) => {
  const { t } = useTranslation();
  const { id, contactHistoryId } = useParams();
  const sectionsRef = useRef<HTMLDivElement | null>(null);
  const { isFetching: isFetchingGetContactDetails, data: contactDetails } =
    useGetContactDetails(id);
  const {
    isFetching: isFetchingGetContactHistoryDetails,
    data: contactHistoryDetails,
  } = useGetContactHistoryDetails({
    enabled: !!contactHistoryId,
    contactHistoryId,
  });
  const activeContactDetails = contactHistoryDetails || contactDetails;

  const renderSections = () => {
    if (
      !activeContactDetails &&
      !isFetchingGetContactDetails &&
      !isFetchingGetContactHistoryDetails
    )
      return null;

    return (
      <div className="mt-20 md:mt-30" ref={sectionsRef}>
        <HeaderTextBlock
          headingText={t("common.contactDetailsView.table.title")}
        />
        <div className="mt-10">
          {isFetchingGetContactDetails || isFetchingGetContactHistoryDetails ? (
            <Shimmer height={600} />
          ) : (
            <ConsentRecordDetail activeContactDetails={activeContactDetails} />
          )}
        </div>
      </div>
    );
  };

  const renderContactHistoryTable = () => (
    <ContactHistoryTable sectionsRef={sectionsRef} channel={channel} />
  );

  const renderHeaderTextBlock = () => (
    <div className="mb-20">
      <HeaderTextBlock
        align="left"
        featureTextColor="primary"
        headingTextColor="secondary"
        featureText={t("common.contactDetailsView.header.feature")}
        headingText={t("common.contactDetailsView.header.heading")}
        descriptionText={t("common.contactDetailsView.header.description")}
      />
    </div>
  );

  const renderBackButton = () => (
    <div className="mt-30">
      <Button
        as="link"
        variant="link"
        icon={faCaretLeft}
        iconPosition="start"
        to={`/${channel === "EMAIL" ? "marketing" : "sms"}/audit-logs`}
        iconClassName="h-20 w-20">
        Back to Audit Logs
      </Button>
    </div>
  );

  return (
    <>
      {renderHeaderTextBlock()}
      {renderContactHistoryTable()}
      {renderSections()}
      {renderBackButton()}
    </>
  );
};

export default ContactDetailsController;
